/**
 * useMerchantProfileActions Hook - Coordinator Pattern Implementation
 *
 * This hook coordinates merchant profile actions:
 * - Update merchant profile (name, description)
 *
 * Pattern: COORDINATOR
 * - Orchestrates profile mutations
 * - Returns Promises (no data storage)
 * - Loading states per action
 * - No computed values or aggregation
 *
 * IMPORTANT: This hook manages profile actions only.
 * For profile data, use the useMerchantProfile aggregator hook.
 */

import { useUpdateMerchantProfile } from '@/generated/hooks';
import type { UpdateMerchantProfileBodyType } from '@zlinkx/shared-v2';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Options for useMerchantProfileActions hook
 */
interface UseMerchantProfileActionsOptions {
	/**
	 * Callback on successful profile update
	 */
	onUpdateSuccess?: () => void;
	/**
	 * Callback on profile update error
	 */
	onUpdateError?: (error: Error) => void;
}

/**
 * Profile actions available
 */
interface MerchantProfileActions {
	/**
	 * Update merchant profile
	 */
	updateProfile: (merchantId: number, data: UpdateMerchantProfileBodyType) => Promise<void>;
}

/**
 * Loading states per action
 */
interface MerchantProfileActionsLoading {
	updateProfile: boolean;
}

/**
 * Return type for useMerchantProfileActions hook
 */
interface UseMerchantProfileActionsReturn extends MerchantProfileActions {
	/**
	 * Loading states per action
	 */
	isLoading: MerchantProfileActionsLoading;
	/**
	 * Check if profile update is pending
	 */
	isUpdating: boolean;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Coordinates merchant profile actions
 *
 * @example
 * ```tsx
 * function MerchantSettingsPage() {
 *   const profileActions = useMerchantProfileActions({
 *     onUpdateSuccess: (result) => {
 *       toast.success('Profile updated successfully');
 *     },
 *     onUpdateError: (error) => {
 *       toast.error('Failed to update profile: ' + error.message);
 *     },
 *   });
 *
 *   const handleSubmit = async (data: FormData) => {
 *     try {
 *       await profileActions.updateProfile(merchantId, {
 *         merchant_name: data.name,
 *         merchant_description: data.description,
 *       });
 *     } catch (error) {
 *       // Error handled by onUpdateError
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       // Form fields here
 *       <button disabled={profileActions.isUpdating}>
 *         {profileActions.isUpdating ? 'Saving...' : 'Save Changes'}
 *       </button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useMerchantProfileActions(
	options: UseMerchantProfileActionsOptions = {},
): UseMerchantProfileActionsReturn {
	const { onUpdateSuccess, onUpdateError } = options;

	// ========================================
	// Generated Hooks (API layer)
	// ========================================

	const updateMutation = useUpdateMerchantProfile({
		onSuccess: () => {
			onUpdateSuccess?.();
		},
		onError: error => {
			onUpdateError?.(error);
		},
	});

	// ========================================
	// Action Functions
	// ========================================

	/**
	 * Update merchant profile
	 */
	const updateProfile = async (
		merchantId: number,
		data: UpdateMerchantProfileBodyType,
	): Promise<void> => {
		await updateMutation.mutateAsync({
			merchantId,
			data,
		});
	};

	// ========================================
	// Return Coordinator Pattern
	// ========================================

	return {
		// Actions
		updateProfile,

		// Loading states
		isLoading: {
			updateProfile: updateMutation.isPending,
		},
		isUpdating: updateMutation.isPending,
	};
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useMerchantProfileActions;
export type {
	UseMerchantProfileActionsOptions,
	MerchantProfileActions,
	MerchantProfileActionsLoading,
	UseMerchantProfileActionsReturn,
	UpdateMerchantProfileBodyType,
};
